import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import validate from './validation'
import * as AppActions from '../../../actions/app'
import * as ContentActions from '../../../actions/content'
import * as CPActions from '../../../actions/contentPush'
import * as NotificationsActions from '../../../actions/notifications'
import { Confirmation as CPConfirmationDOM, ContentPushForm as CPFormDOM } from '../../../components/Screens'
import { defaultExpiryDate } from '../../../reducers/contentPush'
import { ObjectUtils } from '../../../utils'
import { routes } from '../../../constants'
import { propTypes, defaultProps } from './props'

class ContentPushForm extends PureComponent {
    static propTypes = propTypes

    static defaultProps = defaultProps

    /**
     * Constructor
     * @param {object} props
     */
    constructor(props) {
        super(props)

        this.state = {
            id: null,
            isNew: false,
            error: {
                countries: null,
                date_start: null,
                date_end: null,
                group: null,
                level_required: null,
                media: null,
                name: null,
                notes: null,
                purchase_order: null,
            },
            confirmationScreen: false,
            waitingToFinish: false,
            hadPO: false,
        }

        this.onBackForm = this.onBackForm.bind(this)
        this.onChangeEstate = this.onChangeEstate.bind(this)
        this.onChangeFile = this.onChangeFile.bind(this)
        this.onChangeInput = this.onChangeInput.bind(this)
        this.onChangeInputDate = this.onChangeInputDate.bind(this)
        this.onPreSubmit = this.onPreSubmit.bind(this)
        this.onSubmit = this.onSubmit.bind(this)
        this.onSubmitNew = this.onSubmitNew.bind(this)
        this.onSubmitEdit = this.onSubmitEdit.bind(this)
        this.onTextChangePlaylists = this.onTextChangePlaylists.bind(this)
        this.goBack = this.goBack.bind(this)
    }

    /**
     * Transform next props info into the new state
     * @param {object} nextProps
     * @param {object} prevState
     */
    static getDerivedStateFromProps(nextProps, prevState) {
        const newState = {}
        const { isWorking, match: { params: { id } = {} } = {} } = nextProps
        const { waitingToFinish } = prevState

        // Parse isNew and ID
        if (!prevState.id) {
            newState.isNew = !id
            newState.id = id ? parseFloat(id) : -1
        }

        // Check if the API started working on the submit process.
        // If so, set the waitingToFinish flag on.
        // componentDidUpdate will check if API finish its work
        if (isWorking && !waitingToFinish) {
            newState.waitingToFinish = true
        }

        return ObjectUtils.isObjValuesEmpty(newState) ? null : newState
    }

    async componentDidMount() {
        const { clearForm, showContentPush } = this.props
        const { id, isNew } = this.state

        if (!isNew && id > -1) {
            await showContentPush(id)
            const { purchase_order } = this.values
            if (purchase_order) this.setState({ hadPO: true })
        } else {
            clearForm()
        }
    }

    componentDidUpdate() {
        const { error, history, isWorking } = this.props
        const { waitingToFinish } = this.state

        // Check if the component is already waiting for the API work
        // If isWorking value is false, it means the API has stopped
        if (waitingToFinish && !isWorking && !error) {
            history.push(routes.CONTENT_PUSH)
        }
    }

    get onChangeCustom() {
        return {
            date_end: (value) => this.onChangeInputDate('date_end', value),
            date_end_null: () => this.onChangeInputDate('date_end', defaultExpiryDate),
            date_end_remove: () => this.onChangeInputDate('date_end', null),
            date_start: (value) => this.onChangeInputDate('date_start', value),
            media: this.onChangeFile,
            media_remove: () => this.onChangeInput({ target: { name: 'media', value: null } }),
            playlist: (playlistID) => {
                const playlist = this.optionsPlaylists.find(({ key }) => key === playlistID) || {}
                this.onChangeInput({
                    target: {
                        name: 'playlist',
                        value: { id: playlistID, name: playlist.value },
                    },
                })
            },
            playlist_change: this.onTextChangePlaylists,
            playlist_remove: () => this.onChangeInput({ target: { name: 'playlist', value: null } }),
        }
    }

    get optionsPlaylists() {
        const { playlists } = this.props

        return playlists.map(({ id, name }) => ({ key: id, value: name }))
    }

    get selectedPlaylist() {
        const { form } = this.props
        const { playlist } = form
        const { id } = playlist || {}
        return id
    }

    get values() {
        const { form } = this.props

        return {
            ...form,
            expiryDate: new Date(form.expiryDate),
            goLiveDate: new Date(form.goLiveDate),
            group: form.group && form.group.id ? form.group.id.toString() : form.group,
            status: form.status && form.status.id ? form.status.id.toString() : form.status,
            level_required: form.level_required && form.level_required.id ? form.level_required.id.toString() : form.level_required,
        }
    }

    /**
     * On Change estate dropdown
     * @param {object} event
     */
    onChangeEstate({ target: { value } }) {
        const { selectEstate } = this.props
        selectEstate(value)
    }

    /**
     * On Change an input type file
     * @param {object} event
     */
    onChangeFile({ target: { name, files } }) {
        const { editFormField } = this.props
        editFormField(name, files)
    }

    /**
     * On Change any input value
     * @param {object} event
     */
    onChangeInput({ target: { name, value } }) {
        const { editFormField } = this.props
        editFormField(name, value)
    }

    /**
     * Changes the value of a date input
     * @param {string} name Name of the input field
     * @param {date} value Instance of Date with the value
     */
    onChangeInputDate(name, value) {
        this.onChangeInput({
            target: {
                name,
                value: value instanceof Date ? value.getTime() : value,
            },
        })
    }

    onBackForm() {
        this.setState({ confirmationScreen: false })
    }

    onPreSubmit() {
        if (!this.validate()) {
            return
        }

        this.setState({ confirmationScreen: true })
    }

    /**
     * On submit form
     */
    async onSubmit() {
        try {
            const { isNew } = this.state
            const { uploadMedia } = this.props
            const mediaToStore = this.prepareMedia()

            if (mediaToStore) {
                const media = await uploadMedia(mediaToStore.file, mediaToStore)

                if (!media) {
                    throw new Error('No media returned')
                }

                const { id } = media
                this.mediaID = id
            }

            if (isNew) {
                this.onSubmitNew()
            } else {
                this.onSubmitEdit()
            }
        } catch (e) {
            const { notify } = this.props
            notify({ title: 'Error', body: e.message })
        }
    }

    /**
     * On submit new content push
     */
    onSubmitNew() {
        const { createContentPush, form } = this.props

        createContentPush({
            ...form,
            media: this.mediaID,
            playlist: this.selectedPlaylist,
        })
    }

    /**
     * On submit existing content push
     */
    onSubmitEdit() {
        const { editContentPush, form } = this.props
        const { hadPO } = this.state

        editContentPush(
            {
                ...form,
                media: this.mediaID,
                playlist: this.selectedPlaylist,
            },
            hadPO
        )
    }

    /**
     * Fetches new suggestions for the playlists input
     * @param {string} name Name written on the input
     */
    onTextChangePlaylists(name) {
        const { fetchPlaylists } = this.props
        fetchPlaylists(null, { name })
    }

    /**
     * Create media objects
     */
    prepareMedia() {
        const {
            form: { media },
        } = this.props

        if (!media || !media.length) {
            return null
        }

        const file = media[0]

        return {
            size: file.size,
            date_end: null,
            date_start: null,
            name: file.name,
            length: 0,
            file,
        }
    }

    /**
     * Form Validation
     */
    validate() {
        const error = validate(this.values)
        this.setState({ error })
        return ObjectUtils.isObjValuesEmpty(error)
    }

    /**
     * Go back to the last page
     */
    goBack() {
        const { history } = this.props
        history.goBack()
    }

    renderConfirmation() {
        const { isNew, isWorking, error } = this.state

        return (
            <CPConfirmationDOM
                errorNotes={error.notes}
                isNew={isNew}
                isWorking={isWorking}
                onBackForm={this.onBackForm}
                onChangeNotes={this.onChangeInput}
                onSubmit={this.onSubmit}
                values={this.values}
            />
        )
    }

    renderForm() {
        const { countries, estates, contentPushRequiredLevels, selectedEstate, contentPushGroups, contentPushStatus } = this.props
        const { isNew, error, hadPO } = this.state
        const { onChangeCustom } = this

        return (
            <CPFormDOM
                countries={countries}
                errors={error}
                estates={estates.map(({ id, name }) => ({ value: id, name }))}
                goBack={this.goBack}
                isNew={isNew}
                levels={contentPushRequiredLevels}
                statusList={contentPushStatus}
                groups={contentPushGroups}
                onChange={onChangeCustom}
                onChangeDefault={this.onChangeInput}
                onChangeEstate={this.onChangeEstate}
                onSubmit={this.onPreSubmit}
                optionsPlaylist={this.optionsPlaylists}
                selectedEstate={selectedEstate}
                values={this.values}
                hadPO={hadPO}
            />
        )
    }

    render() {
        const { confirmationScreen } = this.state

        if (confirmationScreen) {
            return this.renderConfirmation()
        }

        return this.renderForm()
    }
}

const mapStateToProps = ({
    app: { estates, selectedEstate },
    content: { playlists },
    contentPush: { error, isWorking, list, selected: form, contentPushStatus, contentPushGroups, contentPushRequiredLevels },
    countries: { countries },
}) => ({
    countries,
    form,
    error,
    estates,
    isWorking,
    list,
    selectedEstate,
    playlists,
    contentPushStatus,
    contentPushGroups,
    contentPushRequiredLevels,
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...AppActions,
            ...ContentActions,
            ...CPActions,
            ...NotificationsActions,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(ContentPushForm)
